import React from 'react';

import CreateJellyfishButton from 'app-core/archetype/sidepanel/CreateJellyfishButton';
import {getAllArchivePurposes} from 'archive/ArchivePurposesUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {
  getAppliedFiltersFromUserPreference,
  getAppliedSortsFromUserPreference,
} from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
}

type JellyfishSidepanelListingProps = SidepanelProps & SearchProps;

export function JellyfishSidepanelListing(props: JellyfishSidepanelListingProps): JSX.Element {
  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.JELLYFISH_SIDEPANEL}
        savedSearchSection={SearchSection.JELLYFISH}
        additionalHeaderButtons={<CreateJellyfishButton />}
        availableSortKeys={[SortKey.SORT_NAME_KEY, SortKey.SORT_MODIFIED_AT_KEY, SortKey.SORT_CREATED_AT_KEY]}
        availableTypes={[ModelTypeConstants.JELLYFISH]}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <JellyfishSidepanelCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        extraPrimaryFilterOptions={[]}
        getSecondaryFilterGroups={() => [
          {
            name: 'General',
            filterOptions: [
              {filterKey: FilterKey.NAMES},
              {filterKey: FilterKey.CREATED_AT},
              {filterKey: FilterKey.MODIFIED_AT},
              {filterKey: FilterKey.STARRED},
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {
                  archivePurposes: getAllArchivePurposes(),
                },
              },
            ],
          },
        ]}
        preservedFilterKeys={[]}
        section={Section.JELLYFISH}
      />
    </div>
  );
}

function JellyfishSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="jellyfish"
            text="Jellyfish"
            onClick={() => {
              props.onSearch(SearchUtil.clearSearchParams(props.searchParams, [], getDefaultFilters()));
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs crumbs={[<span key="root">Jellyfish</span>]} settingsIcon={props.settingsComponents} />
  );
}

function getDefaultFilters(): Array<Filter> {
  const filters = getAppliedFiltersFromUserPreference(Section.JELLYFISH);
  if (!filters.find((filter) => filter.key === FilterKey.TYPES)) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.JELLYFISH],
    });
  }
  return filters;
}

export function getDefaultSearchParams(): SearchParams {
  return {
    filters: getDefaultFilters(),
    offset: 0,
    limit: getSelectedPageSize(),
    query: '',
    sorts: getAppliedSortsFromUserPreference(Section.JELLYFISH),
  };
}

export function getURL(
  prevProps: JellyfishSidepanelListingProps,
  props: JellyfishSidepanelListingProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/jellyfish/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(JellyfishSidepanelListing, SearchType.JELLYFISH, SECTIONS.JELLYFISH, {
  defaultSearchParams: getDefaultSearchParams(),
  getNavigationUpdate: getURL,
});
